import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Coffee, ArrowLeft, Delete } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { LanguageMenu } from '@/components/layout/LanguageMenu';
import { Brandmark } from '@/components/layout/Brandmark';
import { useShopLogin, usePinLogin } from '@/hooks/useAuthMutations';
import { toApiError } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';
import type { StaffMember } from '@/types/api';
import { cn } from '@/lib/utils';

const REMEMBER_KEY = 'hoopla-pos:lastShopLogin';

const shopSchema = z.object({
  login: z.string().min(1),
  password: z.string().min(4),
});
type ShopSchema = z.infer<typeof shopSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const shopSession = useAuthStore((s) => s.shopSession);
  const selectedStaff = useAuthStore((s) => s.selectedStaff);
  const setSelectedStaff = useAuthStore((s) => s.setSelectedStaff);
  const clearShopSession = useAuthStore((s) => s.clearShopSession);
  const status = useAuthStore((s) => s.status);

  // Stage is derived from the store so back-nav, a hard refresh, or a
  // successful PIN login all keep the UI coherent without manual bookkeeping.
  const stage: 'shop' | 'pick' | 'pin' = selectedStaff ? 'pin' : shopSession ? 'pick' : 'shop';

  useEffect(() => {
    if (status === 'authenticated') {
      const target = (location.state as { from?: string } | null)?.from ?? '/orders';
      navigate(target, { replace: true });
    }
  }, [status, navigate, location.state]);

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      {/* Left — brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground lg:flex">
        <div className="pointer-events-none absolute -right-32 -top-24 h-[480px] w-[480px] rounded-full border border-primary-foreground/15" />
        <div className="pointer-events-none absolute -left-40 bottom-12 h-[360px] w-[360px] rounded-full border border-primary-foreground/10" />

        <div className="relative flex items-center gap-3">
          <Brandmark className="text-primary-foreground" size={48} />
          <div className="leading-tight">
            <div className="font-display text-3xl">Hoopla</div>
            <div className="label-tracked text-primary-foreground/70">Cassa</div>
          </div>
        </div>

        <div className="relative space-y-5">
          <div className="label-tracked text-primary-foreground/70">For the cashier</div>
          <h1 className="font-display text-[64px] leading-[0.95] text-balance">
            Every order, <em className="not-italic font-normal text-primary-foreground/95">poured</em> with care.
          </h1>
          <p className="max-w-md text-lg leading-relaxed text-primary-foreground/85">
            Hoopla POS is the ticket rail for cafés that don't have a register —
            real-time orders, pickup verification, and an espresso of fiscal data on the side.
          </p>
        </div>

        <div className="relative flex items-center gap-3 text-sm">
          <Coffee className="h-4 w-4 text-primary-foreground/70" />
          <span className="text-primary-foreground/80">
            “We don't serve drinks. We serve mornings.”
          </span>
        </div>
      </div>

      {/* Right — staged login */}
      <div className="relative flex items-center justify-center px-6 py-10">
        <div className="absolute right-6 top-6">
          <LanguageMenu />
        </div>

        <div className="w-full max-w-sm space-y-7">
          <div className="lg:hidden">
            <div className="flex items-center gap-3">
              <Brandmark className="text-primary" size={36} />
              <div className="leading-tight">
                <div className="font-display text-2xl">Hoopla</div>
                <div className="label-tracked">Cassa</div>
              </div>
            </div>
          </div>

          {stage === 'shop' && <ShopStep />}
          {stage === 'pick' && shopSession && (
            <PickStep
              shopName={shopSession.shopName}
              staff={shopSession.staff}
              onPick={setSelectedStaff}
              onBack={clearShopSession}
            />
          )}
          {stage === 'pin' && selectedStaff && (
            <PinStep staff={selectedStaff} onBack={() => setSelectedStaff(null)} />
          )}
        </div>
      </div>
    </div>
  );
}

// Step 1 — the shop signs the terminal in with its own login + password.
function ShopStep() {
  const { t } = useTranslation();
  const shopLogin = useShopLogin();
  const [serverError, setServerError] = useState<string | null>(null);

  const remembered: { login?: string } = (() => {
    try {
      return JSON.parse(localStorage.getItem(REMEMBER_KEY) ?? '{}');
    } catch {
      return {};
    }
  })();

  const form = useForm<ShopSchema>({
    resolver: zodResolver(shopSchema),
    defaultValues: { login: remembered.login ?? '', password: '' },
  });

  const onSubmit = (values: ShopSchema) => {
    setServerError(null);
    shopLogin.mutate(values, {
      onSuccess: () => {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ login: values.login }));
      },
      onError: (err) => {
        const e = toApiError(err);
        setServerError(e.status === 401 ? t('login.error') : e.message);
      },
    });
  };

  return (
    <>
      <div className="space-y-1.5">
        <div className="label-tracked">{t('login.stepShopBadge')}</div>
        <h2 className="font-display text-4xl text-foreground">{t('login.shopTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('login.shopSubtitle')}</p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Field
          id="shopLogin"
          label={t('login.shopLogin')}
          input={
            <Input
              id="shopLogin"
              type="text"
              autoComplete="username"
              autoFocus
              placeholder={t('login.shopLoginPlaceholder')}
              {...form.register('login')}
            />
          }
        />
        <Field
          id="shopPassword"
          label={t('login.shopPassword')}
          input={
            <PasswordInput
              id="shopPassword"
              autoComplete="current-password"
              placeholder={t('login.passwordPlaceholder')}
              toggleLabels={{ show: t('login.showPassword'), hide: t('login.hidePassword') }}
              {...form.register('password')}
            />
          }
        />

        {serverError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {serverError}
          </div>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={shopLogin.isPending}>
          {shopLogin.isPending ? t('login.loading') : t('login.continue')}
        </Button>

        <p className="text-center text-xs text-muted-foreground">{t('login.remember')}</p>
      </form>
    </>
  );
}

// Step 2a — pick which cashier/manager is signing in.
function PickStep({
  shopName,
  staff,
  onPick,
  onBack,
}: {
  shopName: string;
  staff: StaffMember[];
  onPick: (s: StaffMember) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <BackHeader onBack={onBack} backLabel={t('login.changeShop')} />

      <div className="space-y-1.5">
        <div className="label-tracked">{shopName}</div>
        <h2 className="font-display text-4xl text-foreground">{t('login.pickTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('login.pickSubtitle')}</p>
      </div>

      {staff.length === 0 ? (
        <div className="rounded-md border border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
          {t('login.noStaff')}
        </div>
      ) : (
        <ul className="space-y-2">
          {staff.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onPick(s)}
                className="flex w-full items-center justify-between rounded-md border border-border bg-card px-4 py-4 text-left transition hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <div className="space-y-0.5">
                  <div className="text-base font-medium text-foreground">
                    {s.name || t('login.unnamed')}
                  </div>
                  <div className="label-tracked text-[10px] text-muted-foreground">{s.role}</div>
                </div>
                <span className="text-muted-foreground">→</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// Step 2b — the picked cashier enters their 4-digit PIN.
function PinStep({ staff, onBack }: { staff: StaffMember; onBack: () => void }) {
  const { t } = useTranslation();
  const pinLogin = usePinLogin();
  const [pin, setPin] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const submit = (value: string) => {
    pinLogin.mutate(
      { userId: staff.id, pin: value },
      {
        onError: (err) => {
          const e = toApiError(err);
          if (e.status === 401 && /shop session/i.test(e.message)) {
            // Cookie expired mid-flow — bounce back to step 1.
            useAuthStore.getState().clearShopSession();
            return;
          }
          setServerError(e.status === 401 ? t('login.pinError') : e.message);
          setPin('');
        },
      },
    );
  };

  // Auto-submit on the 4th digit — saves a tap. Covers the PIN pad and
  // hardware numeric keypads alike (both route through `push`).
  const push = (d: string) => {
    if (pin.length >= 4 || pinLogin.isPending) return;
    setServerError(null);
    const next = pin + d;
    setPin(next);
    if (next.length === 4) submit(next);
  };

  const pop = () => {
    if (pinLogin.isPending) return;
    setServerError(null);
    setPin((p) => p.slice(0, -1));
  };

  return (
    <>
      <BackHeader onBack={onBack} backLabel={t('login.changeUser')} />

      <div className="space-y-1.5">
        <div className="label-tracked">{staff.role}</div>
        <h2 className="font-display text-4xl text-foreground">
          {staff.name || t('login.unnamed')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('login.pinSubtitle')}</p>
      </div>

      <div className="flex justify-center gap-3" aria-label={t('login.pin')}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              'h-4 w-4 rounded-full border-2',
              pin.length > i ? 'border-primary bg-primary' : 'border-border bg-card',
            )}
          />
        ))}
      </div>

      {serverError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
          {serverError}
        </div>
      )}

      <PinPad onDigit={push} onBackspace={pop} disabled={pinLogin.isPending} />
    </>
  );
}

function PinPad({
  onDigit,
  onBackspace,
  disabled,
}: {
  onDigit: (d: string) => void;
  onBackspace: () => void;
  disabled: boolean;
}) {
  // Accept hardware / USB numeric keypads transparently — many cafés wire one
  // up next to the screen.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (disabled) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        onDigit(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        onBackspace();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDigit, onBackspace, disabled]);

  const Key = ({
    children,
    onClick,
    ariaLabel,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    ariaLabel?: string;
  }) => (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className="flex h-14 items-center justify-center rounded-md border border-border bg-card text-2xl font-medium text-foreground transition hover:border-primary hover:bg-primary/5 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );

  return (
    <div className="grid grid-cols-3 gap-3">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
        <Key key={d} onClick={() => onDigit(d)}>
          {d}
        </Key>
      ))}
      <div />
      <Key onClick={() => onDigit('0')}>0</Key>
      <Key onClick={onBackspace} ariaLabel="Backspace">
        <Delete className="h-5 w-5" />
      </Key>
    </div>
  );
}

function BackHeader({ onBack, backLabel }: { onBack: () => void; backLabel: string }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="-ml-2 inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <ArrowLeft className="h-4 w-4" />
      {backLabel}
    </button>
  );
}

function Field({ id, label, input }: { id: string; label: string; input: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="label-tracked">
        {label}
      </Label>
      {input}
    </div>
  );
}
